import React, { useState, useEffect } from 'react';
import { X, Truck, User, Calendar, MapPin, Activity, FileText, Clock, Building, Phone, QrCode, CreditCard, DollarSign, Tag } from 'lucide-react';
import { formatAppDate } from '../../utils/dateUtils';
import api, { BASE_URL } from '../../utils/api';

const ViewDeliveryModal = ({ delivery, onClose, onTakeAction, canTakeAction }) => {
  const [paymentQrCode, setPaymentQrCode] = useState('');
  const [showQrModal, setShowQrModal] = useState(false);

  useEffect(() => {
    const fetchPaymentQr = async () => {
      try {
        const res = await api.get('/settings');
        if (res.data && res.data.paymentQrCode) {
          setPaymentQrCode(res.data.paymentQrCode);
        }
      } catch (err) {
        console.error("Failed to fetch settings for payment QR", err);
      }
    };
    fetchPaymentQr();
  }, []);

  if (!delivery) return null;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Pending': return <span className="badge" style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontWeight: '600', fontSize: '0.85rem' }}>Pending</span>;
      case 'In Progress': return <span className="badge" style={{ backgroundColor: '#dbeafe', color: '#1e40af', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontWeight: '600', fontSize: '0.85rem' }}>In Progress</span>;
      case 'Delivered': return <span className="badge" style={{ backgroundColor: '#d1fae5', color: '#065f46', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontWeight: '600', fontSize: '0.85rem' }}>Delivered</span>;
      case 'Cancelled': return <span className="badge" style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontWeight: '600', fontSize: '0.85rem' }}>Cancelled</span>;
      default: return <span className="badge bg-gray-200 text-gray-800" style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', fontWeight: '600', fontSize: '0.85rem' }}>{status}</span>;
    }
  };

  const getQrImageUrl = () => {
    if (!paymentQrCode || typeof paymentQrCode !== 'string') return null;
    const val = paymentQrCode.trim();
    if (!val) return null;
    if (val.startsWith('data:') || val.startsWith('http://') || val.startsWith('https://')) {
      return val;
    }
    const cleanPath = val.startsWith('/') ? val : `/${val}`;
    return `${BASE_URL}${cleanPath}`;
  };

  const rawLogs = delivery.logs || [];
  // Filter out redundant "Status changed to X" logs if a corresponding "Action taken: X" log exists
  const logs = rawLogs.filter((log) => {
    if (log.action && log.action.startsWith('Status changed to ')) {
      const statusName = log.action.replace('Status changed to ', '').trim();
      const hasActionLog = rawLogs.some(other => 
        other !== log && 
        other.action && 
        other.action.includes(`Action taken: ${statusName}`)
      );
      if (hasActionLog) return false;
    }
    return true;
  });

  const partyName = delivery.partyName || delivery.lead?.companyName;
  const partyPhone = delivery.partyPhone || delivery.lead?.phone;
  const partyEmail = delivery.partyEmail || delivery.lead?.email;
  const rawAddress = delivery.partyAddress || delivery.lead?.address || '';
  
  // Extract GPS coordinates if present on delivery model or legacy address string
  let lat = delivery.latitude;
  let lng = delivery.longitude;
  if ((!lat || !lng) && rawAddress) {
    const gpsMatch = rawAddress.match(/\[GPS:\s*Lat:\s*([\d.-]+),\s*Lon:\s*([\d.-]+)\]/i);
    if (gpsMatch) {
      lat = gpsMatch[1];
      lng = gpsMatch[2];
    }
  }

  // Clean address string by removing legacy GPS tag
  const cleanAddress = rawAddress ? rawAddress.replace(/\s*\[GPS:\s*Lat:\s*[\d.-]+,\s*Lon:\s*[\d.-]+\]/gi, '').trim() : '';
  const partyCity = delivery.partyCity || delivery.lead?.city;
  const partyState = delivery.partyState || delivery.lead?.state;
  const partyPincode = delivery.partyPincode || delivery.lead?.pincode;
  const fullAddress = [cleanAddress, partyCity, partyState, partyPincode].filter(Boolean).join(', ');

  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '850px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
        
        {/* Header */}
        <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ padding: '0.5rem', borderRadius: '8px', backgroundColor: '#e0f2fe', color: '#0284c7' }}>
              <Truck size={22} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>
                Delivery #{delivery.id} {delivery.title ? `- ${delivery.title}` : ''}
              </h2>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
                Created on {delivery.createdAt ? formatAppDate(delivery.createdAt) : '-'}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', overflowY: 'auto', flex: 1 }}>
          
          {/* Left Column: Delivery, Order & Party Info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            
            {/* Status & Scheduled Card */}
            <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#64748b' }}>STATUS</span>
                {getStatusBadge(delivery.status)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#334155' }}>
                <Calendar size={16} color="#0284c7" />
                <span style={{ fontWeight: '500' }}>Scheduled Date:</span>
                <span>{delivery.scheduledDate ? formatAppDate(delivery.scheduledDate) : 'Not scheduled'}</span>
              </div>
            </div>

            {/* Order & Amount Details */}
            {(delivery.orderNo || delivery.amount !== undefined || delivery.orderDate) && (
              <div style={{ padding: '1rem', borderRadius: '8px', border: '1px solid #bae6fd', backgroundColor: '#f0f9ff' }}>
                <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: '600', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CreditCard size={18} color="#0284c7" /> Order & Payment Info
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.9rem' }}>
                  {delivery.orderNo && (
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>ORDER NO.</div>
                      <div style={{ fontWeight: '700', color: '#0f172a' }}>{delivery.orderNo}</div>
                    </div>
                  )}
                  {delivery.orderDate && (
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>ORDER DATE</div>
                      <div style={{ fontWeight: '500', color: '#334155' }}>{formatAppDate(delivery.orderDate)}</div>
                    </div>
                  )}
                  {delivery.amount !== undefined && delivery.amount !== null && (
                    <div style={{ gridColumn: 'span 2', marginTop: '0.25rem', paddingTop: '0.5rem', borderTop: '1px dashed #cbd5e1' }}>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>AMOUNT TO PAY</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#059669' }}>
                        ₹{Number(delivery.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Party / Lead Details */}
            <div style={{ padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white' }}>
              <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Building size={18} color="#0284c7" /> Party / Customer Details
              </h3>
              {partyName || partyPhone || partyEmail || fullAddress ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem', color: '#334155' }}>
                  {partyName && <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '0.95rem' }}>{partyName}</div>}
                  {delivery.lead?.contactPerson && (
                    <div>Contact Person: <span style={{ fontWeight: '500' }}>{delivery.lead.contactPerson}</span></div>
                  )}
                  {partyPhone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Phone size={14} color="#64748b" /> <a href={`tel:${partyPhone}`} style={{ color: '#0284c7', textDecoration: 'none', fontWeight: '500' }}>{partyPhone}</a>
                    </div>
                  )}
                  {partyEmail && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span style={{ fontSize: '0.9rem' }}>✉️</span> <a href={`mailto:${partyEmail}`} style={{ color: '#0284c7', textDecoration: 'none', fontWeight: '500' }}>{partyEmail}</a>
                    </div>
                  )}
                  {fullAddress && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginTop: '0.25rem' }}>
                      <MapPin size={14} color="#64748b" style={{ marginTop: '0.2rem', flexShrink: 0 }} />
                      <span>{fullAddress}</span>
                    </div>
                  )}

                  {/* Separate Google Maps GPS Coordinates Box */}
                  {(lat && lng) && (
                    <div style={{ padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #bae6fd', backgroundColor: '#f0f9ff', marginTop: '0.5rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.35rem' }}>
                        <MapPin size={16} color="#0284c7" /> Location Coordinates
                      </div>
                      <div style={{ fontSize: '0.825rem', color: '#334155', marginBottom: '0.5rem' }}>
                        Lat: <strong>{lat}</strong>, Lon: <strong>{lng}</strong>
                      </div>
                      <a
                        href={`https://www.google.com/maps?q=${lat},${lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          padding: '0.4rem 0.85rem',
                          backgroundColor: '#0284c7',
                          color: 'white',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          fontSize: '0.825rem',
                          fontWeight: '600'
                        }}
                      >
                        🗺️ Open in Google Maps
                      </a>
                    </div>
                  )}

                </div>
              ) : (
                <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.9rem' }}>General Delivery (No party info specified)</span>
              )}
            </div>

            {/* Payment QR Code Section */}
            {paymentQrCode && (
              <div style={{ padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fcfcfc', textAlign: 'center' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                  <QrCode size={18} color="#0284c7" /> Payment QR Code
                </h3>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.75rem', color: '#64748b' }}>
                  Scan to pay {delivery.amount ? `₹${Number(delivery.amount).toLocaleString('en-IN')}` : 'for this delivery'}
                </p>
                <div style={{ cursor: 'pointer', display: 'inline-block' }} onClick={() => setShowQrModal(true)} title="Click to enlarge QR Code">
                  <img 
                    src={getQrImageUrl()} 
                    alt="Payment QR Code" 
                    style={{ maxWidth: '180px', maxHeight: '180px', objectFit: 'contain', margin: '0 auto', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '6px', backgroundColor: 'white', display: 'block', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} 
                  />
                  <div style={{ fontSize: '0.75rem', color: '#0284c7', marginTop: '0.4rem', fontWeight: '600' }}>
                    🔍 Click to enlarge QR Code
                  </div>
                </div>
              </div>
            )}

            {/* Assigned Delivery Boy */}
            <div style={{ padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white' }}>
              <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <User size={18} color="#0284c7" /> Assigned Delivery Boy
              </h3>
              {delivery.deliveryBoy ? (
                <div style={{ fontSize: '0.9rem', color: '#334155' }}>
                  <div style={{ fontWeight: '600', color: '#0f172a' }}>{delivery.deliveryBoy.name}</div>
                  {delivery.deliveryBoy.phone && (
                    <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.2rem' }}>
                      Phone: {delivery.deliveryBoy.phone}
                    </div>
                  )}
                </div>
              ) : (
                <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.9rem' }}>Unassigned</span>
              )}
            </div>

            {/* Details / Instructions */}
            {delivery.details && (
              <div style={{ padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FileText size={16} /> Details / Instructions
                </h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#334155', whiteSpace: 'pre-wrap' }}>
                  {delivery.details}
                </p>
              </div>
            )}
          </div>

          {/* Right Column: Timeline Activity */}
          <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white', padding: '1rem' }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Activity size={18} color="#0284c7" /> Activity Timeline
            </h3>

            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '420px', paddingRight: '0.5rem' }}>
              {logs.length > 0 ? (
                <div style={{ position: 'relative', paddingLeft: '1.25rem' }}>
                  {/* Vertical Line */}
                  <div style={{ position: 'absolute', left: '6px', top: '8px', bottom: '8px', width: '2px', backgroundColor: '#e2e8f0' }} />

                  {logs.map((log, idx) => (
                    <div key={log.id || idx} style={{ position: 'relative', marginBottom: '1.25rem' }}>
                      {/* Circle Indicator */}
                      <div style={{ position: 'absolute', left: '-1.25rem', top: '4px', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: idx === 0 ? '#0284c7' : '#94a3b8', border: '2px solid white' }} />

                      <div style={{ backgroundColor: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.25rem' }}>
                          <span style={{ fontWeight: '600', fontSize: '0.875rem', color: '#0f172a' }}>
                            {log.action}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                            <Clock size={12} /> {log.createdAt ? formatAppDate(log.createdAt) : ''}
                          </span>
                        </div>

                        {log.notes && (
                          <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.825rem', color: '#475569', whiteSpace: 'pre-wrap', backgroundColor: 'white', padding: '0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                            {log.notes}
                          </p>
                        )}

                        <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                          By: {log.createdBy || 'System'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#94a3b8', fontStyle: 'italic', fontSize: '0.9rem' }}>
                  No activity logs recorded yet.
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', backgroundColor: '#f9fafb', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', flexShrink: 0 }}>
          {canTakeAction && onTakeAction && !['delivered', 'cancelled'].includes((delivery.status || '').trim().toLowerCase()) && (
            <button 
              type="button" 
              onClick={() => { onClose(); onTakeAction(delivery); }}
              className="btn btn-primary" 
              style={{ padding: '0.5rem 1.25rem', border: 'none', borderRadius: '8px', backgroundColor: '#0284c7', color: 'white', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <Activity size={16} /> Take Action
            </button>
          )}
          <button 
            type="button" 
            onClick={onClose} 
            className="btn" 
            style={{ padding: '0.5rem 1.25rem', border: '1px solid #cbd5e1', borderRadius: '8px', backgroundColor: 'white', color: '#334155', cursor: 'pointer', fontWeight: '500' }}
          >
            Close
          </button>
        </div>

      </div>

      {/* Enlarged Payment QR Code Modal */}
      {showQrModal && (
        <div 
          className="modal-overlay" 
          onClick={() => setShowQrModal(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: 'white', borderRadius: '16px', padding: '1.75rem', maxWidth: '420px', width: '100%', textAlign: 'center', position: 'relative', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
          >
            <button 
              type="button"
              onClick={() => setShowQrModal(false)} 
              style={{ position: 'absolute', top: '1rem', right: '1rem', background: '#f1f5f9', border: 'none', borderRadius: '50%', cursor: 'pointer', color: '#64748b', padding: '0.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <X size={20} />
            </button>

            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <QrCode size={24} color="#0284c7" /> Payment QR Code
            </h3>

            {delivery.amount !== undefined && delivery.amount !== null && (
              <div style={{ fontSize: '1.15rem', fontWeight: '800', color: '#059669', marginBottom: '0.75rem' }}>
                Amount to Pay: ₹{Number(delivery.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
            )}

            <div style={{ padding: '1rem', backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', display: 'inline-block', margin: '0.5rem 0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
              <img 
                src={getQrImageUrl()} 
                alt="Payment QR Code Enlarged" 
                style={{ width: '280px', height: '280px', objectFit: 'contain', display: 'block' }} 
              />
            </div>

            <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>
              Scan using any UPI App (GPay, PhonePe, Paytm, BHIM)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewDeliveryModal;
